<template>
    <div class="printable-schedule-container">
        <DotsLoading v-if="loading" />
        <template v-else>
            <p v-if="routeError" class="callout error">{{ routeError }}</p>
            <div v-show="hasErrors" class="callout error">
                <p>{{ t('form_errors', { count: errorsCount }) }}</p>
                <ul ref="errorList">
                    <template v-for="[key, value] in Object.entries(errors)">
                        <li v-if="value" :key="`error-${key}`">
                            <button type="button" class="error-anchor" @click="scrollToInput(`#error-${key}`)">{{ value }}</button>
                        </li>
                    </template>
                </ul>
            </div>
            <form class="form-schedule" @submit="onSubmit">
                <fieldset>
                    <legend v-once>
                        <span class="step-wrapper">
                            <span class="step">{{ getStepIndex() }}</span>
                            {{ t('route_and_direction') }}
                        </span>
                    </legend>
                    <div class="route-info-container">
                        <div v-if="route">
                            <b>{{ t('route') }}</b>
                            <div>{{ route.noParcours }}</div>
                        </div>
                        <div v-if="route">
                            <b>Direction</b>
                            <div>{{ direction.description }}</div>
                        </div>
                        <div class="route-button-container" :class="{ 'btn-is-open': routeSearchVisible }">
                            <button type="button" ref="selectorTrigger" class="button btn-direction" @click="toggleRouteSearch">
                                <template v-if="route !== null">{{ t('change_route') }}</template>
                                <template v-else>{{ t('choose_route') }}</template>
                            </button>
                            <RouteSelector
                                ref="selector"
                                v-if="routeSearchVisible"
                                class="route-search-container"
                                :aria-expanded="routeSearchVisible.toString()"
                                :trigger-ref="selectorTrigger"
                                :show-stops="false"
                                @close="routeSearchVisible = false"
                            ></RouteSelector>
                        </div>
                    </div>
                </fieldset>
                <template v-if="route">
                    <fieldset v-if="nextAvailablePeriod">
                        <legend v-once>
                            <span class="step-wrapper">
                                <span class="step">{{ getStepIndex() }}</span>
                                {{ t('desired_period') }}
                            </span>
                        </legend>
                        <p>{{ t('schedules_will_change', { date: getEffectiveDate(periods[1].dateDebutPeriode) }) }}</p>
                        <div class="radio-wrapper">
                            <input id="period-1" v-model="form.period" :value="0" type="radio" />
                            <label for="period-1">{{
                                t('for_current_period', { date: getEffectiveDate(periods[0].dateFinPeriode) })
                            }}</label>
                        </div>
                        <div class="radio-wrapper">
                            <input id="period-2" v-model="form.period" :value="1" type="radio" />
                            <label for="period-2">{{
                                t('for_future_period', { date: getEffectiveDate(periods[1].dateDebutPeriode) })
                            }}</label>
                        </div>
                    </fieldset>
                    <fieldset id="error-schedule">
                        <legend v-once>
                            <span class="step-wrapper">
                                <span class="step">{{ getStepIndex() }}</span>
                                {{ t('schedule_type') }}
                            </span>
                        </legend>
                        <div class="schedule-type-container">
                            <div class="schedule-days">
                                <b class="schedule-type-title">{{ t('schedule_of') }}</b>
                                <div class="checkbox-wrapper">
                                    <input
                                        id="weekdays"
                                        v-model="form.schedule"
                                        type="checkbox"
                                        value="weekdays"
                                        :disabled="!isScheduleItemDisabled('weekdays')"
                                    />
                                    <label for="weekdays" :class="{ 'is-invalid-label': errors.schedule }">{{
                                        t('monday_to_friday')
                                    }}</label>
                                </div>
                                <div class="checkbox-wrapper">
                                    <input
                                        id="saturday"
                                        v-model="form.schedule"
                                        type="checkbox"
                                        value="saturday"
                                        :disabled="!isScheduleItemDisabled('saturday')"
                                    />
                                    <label for="saturday" :class="{ 'is-invalid-label': errors.schedule }">{{
                                        t('saturday')
                                    }}</label>
                                </div>
                                <div class="checkbox-wrapper">
                                    <input
                                        id="sunday"
                                        v-model="form.schedule"
                                        type="checkbox"
                                        value="sunday"
                                        :disabled="!isScheduleItemDisabled('sunday')"
                                    />
                                    <label for="sunday" :class="{ 'is-invalid-label': errors.schedule }"
                                        >{{ t('sunday') }}
                                    </label>
                                </div>
                                <span v-if="errors.schedule" class="form-error is-visible">
                                    {{ errors.schedule }}
                                </span>
                            </div>
                            <div>
                                <b class="schedule-type-title">{{ t('time_of_day') }}</b>
                                <div class="checkbox-wrapper">
                                    <input id="allDay" v-model="form.time.allDay" type="checkbox" />
                                    <label for="allDay">{{ t('whole_day') }}</label>
                                </div>
                                <div class="spacer">
                                    <b>{{ t('or') }}</b>
                                </div>
                                <div class="form-select">
                                    <label>
                                        {{ t('from') }}
                                        <select v-model="form.time.from" :disabled="form.time.allDay">
                                            <option
                                                v-for="(hour, index) in hours"
                                                :key="`start-${hour}`"
                                                :disabled="isToValueDisabled(index)"
                                            >
                                                {{ hour }}
                                            </option>
                                        </select>
                                    </label>
                                    <label>
                                        {{ t('to') }}
                                        <select v-model="form.time.to" :disabled="form.time.allDay">
                                            <option
                                                v-for="(hour, index) in hours"
                                                :key="`end-${hour}`"
                                                :disabled="isFromValueDisabled(index)"
                                            >
                                                {{ hour }}
                                            </option>
                                        </select>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <p
                            v-if="hasScheduleItemNotAvailable()"
                            class="schedule-item-info"
                            v-html="t('no_service_for_item')"
                        ></p>
                    </fieldset>
                    <template v-if="stops.length !== 0">
                        <fieldset>
                            <legend v-once>
                                <span class="step-wrapper">
                                    <span class="step">{{ getStepIndex() }}</span>
                                    {{ t('font_size') }}
                                </span>
                            </legend>
                            <div class="font-size-wrapper radio-wrapper">
                                <input id="size-1" v-model="form.size" type="radio" :value="8" />
                                <label for="size-1" class="label small">{{ t('small') }} (8pts)</label>
                            </div>
                            <div class="font-size-wrapper radio-wrapper">
                                <input id="size-2" v-model="form.size" type="radio" :value="12" />
                                <label for="size-2" class="label medium">{{ t('medium') }} (12pts)</label>
                            </div>
                            <div class="font-size-wrapper radio-wrapper">
                                <input id="size-3" v-model="form.size" type="radio" :value="16" />
                                <label for="size-3" class="label large">{{ t('large') }} (16pts)</label>
                            </div>
                        </fieldset>
                        <fieldset id="error-selectedStops">
                            <legend v-once>
                                <span class="step-wrapper">
                                    <span class="step">{{ getStepIndex() }}</span>
                                    <span>
                                        {{ t('choose_your_stops') }}
                                        <small>({{ t('max_stops') }})</small>
                                    </span>
                                </span>
                            </legend>
                            <p class="stops-instruction">
                                {{ t('on_list_or_map') }}
                            </p>
                            <span v-if="errors.selectedStops" class="form-error is-visible selected-stops-error">
                                {{ errors.selectedStops }}
                            </span>
                            <TabList class="stops-tabs" :enabled="!isDesktop" :tabs="printableTabs">
                                <template #tab0>
                                    <h2 class="stop-title show-for-large">{{ t('list') }}</h2>
                                    <ul class="stop-list no-bullet">
                                        <li v-for="stop in stops" :key="`stop-${stop.noArret}`" class="stop-item">
                                            <input
                                                :id="`stop-${stop.noArret}`"
                                                v-model="form.selectedStops"
                                                type="checkbox"
                                                :value="stop.noArret"
                                                :disabled="isStopDisabled(stop.noArret)"
                                            />
                                            <label
                                                class="stop-label"
                                                :class="{ 'is-invalid-label': errors.selectedStops }"
                                                :for="`stop-${stop.noArret}`"
                                            >
                                                <div class="stop-inner">
                                                    <span class="stop-name">{{ stop.noArret }} - {{ stop.nom }}</span>
                                                    <span class="stop-description">{{ stop.description }}</span>
                                                </div>
                                                <img
                                                    v-if="route.accessible && stop.accessible"
                                                    src="@@/img/accessible.svg"
                                                    width="18"
                                                    height="18"
                                                />
                                                <span v-if="route.accessible && stop.accessible" class="visuallyhidden">{{
                                                    $t('a11y.accessible.accessible_stop')
                                                }}</span>
                                            </label>
                                        </li>
                                    </ul>
                                </template>
                                <template #tab1>
                                    <h2 class="map-title show-for-large">{{ t('map') }}</h2>
                                    <PrintableScheduleMap
                                        class="map-container"
                                        :route="route"
                                        :direction="direction"
                                        :selected-stops="form.selectedStops"
                                        :selected-date="effectiveDate"
                                        @selectStop="onSelect"
                                    ></PrintableScheduleMap>
                                </template>
                            </TabList>
                        </fieldset>
                        <fieldset>
                            <legend v-once>
                                <span class="step-wrapper">
                                    <span class="step">{{ getStepIndex() }}</span>
                                    {{ t('add_trace') }}
                                </span>
                            </legend>
                            <div class="flex-container flex-dir-column medium-flex-dir-row">
                                <div class="radio-wrapper">
                                    <input id="trace-1" v-model="form.trace" type="radio" :value="true" />
                                    <label for="trace-1" class="item">
                                        {{ t('yes_trace') }}
                                    </label>
                                </div>
                                <div class="radio-wrapper">
                                    <input id="trace-2" v-model="form.trace" type="radio" :value="false" />
                                    <label for="trace-2" class="item">
                                        {{ t('no_trace') }}
                                    </label>
                                </div>
                            </div>
                        </fieldset>
                        <button type="submit" class="button">
                            {{ t('create_custom_schedule') }}
                        </button>
                    </template>
                    <div v-else class="no-stops-message">
                        <p>
                            {{ t('no_schedule_for_chosen_period') }}
                        </p>
                        <p>{{ t('try_again_later') }}</p>
                    </div>
                </template>
            </form>
        </template>
    </div>
</template>

<script setup>
import { useWindowSize, useDebounce } from '@vueuse/core';
import { invert } from 'lodash';
import { useI18n } from 'vue-i18n';
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { isAfter, subDays } from '@@/js/utils/dateFns';
import { useSchedulesStore } from '@@/js/stores/schedules';
import { useEffectiveDate } from '@/schedules/composables/useEffectiveDate';
import fr from '@/i18n/printable/fr.json';
import en from '@/i18n/printable/en.json';
import slugify from '@@/js/utils/slugify';
import DotsLoading from '@/shared/DotsLoading';
import PrintableScheduleMap from '@/printable-schedule/components/PrintableScheduleMap';
import RouteSelector from '@/shared/RouteSelector';
import TabList from '@/shared/TabList';

import IconList from '@@/icons/list.svg';
import IconMapPin from '@@/icons/map-pin.svg';

const selectorTrigger = ref();

const { getEffectiveDate } = useEffectiveDate();
const schedulesStore = useSchedulesStore();
const { fetchSchedulesData, fetchRoute, getDirectionDetails, getApplicationPeriods, fetchStops } = schedulesStore;

function getDirectionFromCode(route, code) {
    const codesDirections = invert({
        codeDirectionPrincipale: route.codeDirectionPrincipale,
        codeDirectionRetour: route.codeDirectionRetour,
    });
    const codeDirectionType = codesDirections[code].replace('codeDirection', '');
    return route[`descriptionDirection${codeDirectionType}`];
}

let step = 0;

const { t, locale } = useI18n({
    messages: {
        fr,
        en,
    },
});

const { width } = useWindowSize();
const debouncedWidth = useDebounce(width, 100);
const isDesktop = computed(() => debouncedWidth.value > 1024);

const printableTabs = [
    { name: t('list'), icon: IconList },
    { name: t('map'), icon: IconMapPin },
];

const route = ref(false);
const periods = ref([]);
const direction = ref(null);
const stops = ref([]);
const routeError = ref(null);
const hours = ref([]);
const form = ref({
    schedule: [],
    time: {
        allDay: true,
        from: '08:00',
        to: '17:00',
    },
    period: 0,
    size: 12,
    selectedStops: [],
    trace: true,
});
const errors = ref({
    schedule: '',
    selectedStops: '',
});
const errorList = ref();
const hasErrors = computed(() => {
    return Object.values(errors.value).some((error) => error !== '');
});
const errorsCount = computed(() => {
    return Object.values(errors.value).filter((error) => error !== '').length;
});

const routeSearchVisible = ref(false);
const effectiveDate = ref(new Date());

const nextAvailablePeriod = computed(() => {
    if (periods.value && periods.value.length > 1) {
        const today = new Date();
        return isAfter(today, subDays(periods.value[1].dateDebutPeriode, 14));
    }
    return false;
});

const vRoute = useRoute();
const router = useRouter();
const { params, name } = vRoute;

watch(form.value.period, async () => {
    let newEffectiveDate = new Date();
    if (
        periods.value[form.value.period] &&
        new Date(periods.value[form.value.period].dateDebutPeriode) > newEffectiveDate
    ) {
        newEffectiveDate = new Date(periods.value[form.value.period].dateDebutPeriode);
    }
    effectiveDate.value = newEffectiveDate;
    route.value = await fetchRoute(params.route, effectiveDate.value);
    stops.value = await fetchStops(
        route.value.noParcours,
        direction.value.code,
        effectiveDate.value,
    );
    direction.value = getDirectionDetails(
        route.value,
        getDirectionFromCode(route.value, direction.value.code),
    );
    const stopsList = stops.value.map((stop) => stop.noArret);
    form.value.selectedStops = form.value.selectedStops.filter((stop) => {
        return stopsList.includes(stop);
    });
    form.value.schedule = form.value.schedule.filter((val) => isScheduleItemDisabled(val));
});

function getStepIndex() {
    step += 1;
    return step;
}

async function onSubmit(event) {
    event.preventDefault();
    if (!isFormValid()) {
        await nextTick();
        const firstError = errorList.value?.querySelector('button');
        if (firstError) {
            firstError.scrollIntoView({ behavior: 'smooth' });
            firstError.focus();
        }
        return;
    }

    const url = router.resolve(
        {
            name: locale.value === 'en' ? 'printable-schedule-print-proxy-en' : 'printable-schedule-print-proxy',
            params: {
                route: route.value.noParcours,
                direction: slugify(direction.value.description),
            },
            query: {
                a: form.value.selectedStops,
                ta: form.value.time.allDay,
                tf: form.value.time.from,
                tt: form.value.time.to,
                s: form.value.size,
                t: form.value.trace,
                sc: form.value.schedule,
                p: nextAvailablePeriod.value ? form.value.period : undefined,
            },
        },
    );
    window.open(url.href, 'popup', 'width=1056,height=800,resizable=1,scrollbars=1');
}

function isFormValid() {
    let valid = true;
    resetFormError();
    // Valider l'horaire
    if (form.value.schedule.length === 0) {
        errors.value.schedule = t('select_day_error');
        valid = false;
    }
    // Valider les arrêts
    if (form.value.selectedStops.length === 0) {
        errors.value.selectedStops = t('select_stop_error');
        valid = false;
    }
    return valid;
}

async function scrollToInput(selector) {
    await nextTick();
    const element = document.querySelector(selector);
    if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        element.focus();
    }
}

function resetFormError() {
    errors.value.schedule = '';
    errors.value.selectedStops = '';
}

function isStopDisabled(noStop) {
    return form.value.selectedStops.length >= 6 && !form.value.selectedStops.includes(noStop);
}

function isScheduleItemDisabled(period) {
    switch (period) {
        case 'weekdays': {
            const weekdays = route.value.jours.split('').splice(1, 5);
            return Boolean(weekdays.includes('1'));
        }
        case 'saturday':
            if (
                periods.value &&
                periods.value[form.value.period] &&
                new Date(periods.value[form.value.period].dateFinPeriode) < getNextDayOfWeek(6)
            ) {
                return false;
            }
            return Boolean(parseInt(route.value.jours.split('')[6]));
        case 'sunday':
            if (
                periods.value &&
                periods.value[form.value.period] &&
                new Date(periods.value[form.value.period].dateFinPeriode) < getNextDayOfWeek(0)
            ) {
                return false;
            }
            return Boolean(parseInt(route.value.jours.split('')[0]));
    }
}

function hasScheduleItemNotAvailable() {
    if (
        (Boolean(parseInt(route.value.jours.split('')[6])) &&
            periods.value &&
            periods.value[form.value.period] &&
            new Date(periods.value[form.value.period].dateFinPeriode) < getNextDayOfWeek(6)) ||
        (Boolean(parseInt(route.value.jours.split('')[0])) &&
            periods.value &&
            periods.value[form.value.period] &&
            new Date(periods.value[form.value.period].dateFinPeriode) < getNextDayOfWeek(0))
    ) {
        return true;
    }
}

function getNextDayOfWeek(dayOfWeek) {
    const resultDate = new Date();
    resultDate.setDate(resultDate.getDate() + ((7 + dayOfWeek - resultDate.getDay()) % 7));
    return resultDate;
}

function isFromValueDisabled(index, direction) {
    return index <= hours.value.findIndex((i) => i === form.value.time.from);
}

function isToValueDisabled(index, direction) {
    return index >= hours.value.findIndex((i) => i === form.value.time.to);
}

function onSelect(stop) {
    const stopIndex = form.value.selectedStops.indexOf(stop);
    if (stopIndex !== -1) {
        form.value.selectedStops.splice(stopIndex, 1);
    } else if (form.value.selectedStops.length < 6) {
        form.value.selectedStops.push(stop);
    }
}

const selector = ref();

async function toggleRouteSearch() {
    if (!routeSearchVisible.value) {
        routeSearchVisible.value = true;
        await nextTick();
        if (selector.value && selector.value.focus) {
            selector.value.focus();
        }
    } else {
        routeSearchVisible.value = false;
    }
}

const loading = ref(true);
onMounted(async () => {
    await fetchSchedulesData();
    if (params.route) {
        route.value = await fetchRoute(params.route);
        if (!route.value) {
            routeError.value = t('printing_not_available');
        } else {
            direction.value = await getDirectionDetails(route.value, params.direction);
            if (!params.direction) {
                router.replace({ name, params: { ...params, direction: slugify(direction.value.description) } });
            }
            periods.value = await getApplicationPeriods();
            stops.value = await fetchStops(
                route.value.noParcours,
                direction.value.code,
                effectiveDate.value,
            );
        }
    }
    step = 0;
    for (let i = 0; i < 24; i++) {
        hours.value.push(`${i.toString().padStart(2, '0')}:00`);
        hours.value.push(`${i.toString().padStart(2, '0')}:30`);
    }
    hours.value = hours.value.concat(
        hours.value.splice(
            0,
            hours.value.findIndex((i) => i === '05:00'),
        ),
    );
    loading.value = false;
});
</script>

<style lang="scss" scoped>
.callout {
    margin-bottom: 24px;
}
.error-anchor {
    @include rem-typo(16px);
    color: $color-blue-60;
    text-decoration: underline;
    background: none;
    border: 0;
    font-weight: 600;
}
.printable-schedule-container {
    margin-top: 32px;
}
.intro {
    @include rem-typo(20px, 32px);
    font-weight: 300;
}
.form-schedule {
    padding: 24px;
    border-radius: 20px;
    background: $color-white;
    @include breakpoint(medium) {
        padding: 24px 40px;
    }
}
legend {
    @include rem-typo(18px, 24px);
    display: inline-flex;
    align-items: center;
    margin-bottom: 16px;
    font-weight: 700;
    @include breakpoint(medium) {
        @include rem-typo(24px, 24px);
        margin-left: (-32px - 16px);
    }
}
fieldset {
    margin-bottom: 24px;
    @include breakpoint(medium) {
        margin-bottom: 48px;
        margin-left: (32px + 16px);
    }
}
.form-select {
    display: flex;
    gap: 12px;
    select {
        margin-left: 8px;
    }
}
.spacer {
    margin: 8px 0;
}
.route-info-container {
    display: flex;
    flex-wrap: wrap;
    gap: 32px;
}
.step-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;
    line-height: 1.2;
}
.step {
    @include rem-typo(20px, 32px);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    font-weight: 400;
    text-align: center;
    color: $color-white;
    border-radius: 50%;
    background: $color-blue-60;
    flex-shrink: 0;
}
.schedule-type-container {
    display: flex;
    flex-direction: column;
    gap: 24px;
    @include breakpoint(medium) {
        flex-direction: row;
    }
}
.schedule-days {
    border-bottom: 2px solid $color-black-2;
    padding-bottom: 8px;
    @include breakpoint(medium) {
        border-bottom: 0;
        border-right: 2px solid $color-black-2;
        padding: 0 24px 0 0;
    }
}
.schedule-type-title {
    margin-bottom: 16px;
    display: block;
}
.schedule-item-info {
    @include rem-typo(16px, 18px);
    margin-top: 20px;
    font-weight: 300;
    @include breakpoint(medium) {
        @include rem-typo(18px, 22px);
    }
}
.stop-item {
    display: flex;
    gap: 8px;
    padding: 16px;
    border-bottom: 1px solid $color-black-10;
    @include breakpoint(large) {
        padding: 16px 0;
    }
    &:last-child {
        border-bottom: none;
    }
}
.stop-label {
    display: flex;
    flex-grow: 1;
    gap: 16px;
}
.stop-inner {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    margin-top: -2px;
    align-self: normal;
}
.stop-name {
    @include rem-typo(18px, 24px);
}
.stop-description {
    @include rem-typo(12px, 16px);
    color: $color-black-50;
}
.font-size-wrapper {
    height: 24px;
    justify-content: center;
    input {
        top: 50%;
        transform: translateY(-50%);
    }
}
.small {
    font-size: 8pt;
    &::before {
        top: 1px;
    }
}
.medium {
    font-size: 12pt;
    &::before {
        top: 4px;
    }
}
.large {
    font-size: 16pt;
    &::before {
        top: 9px;
    }
}
.form-error {
    margin: 0;
    &.selected-stops-error {
        display: block;
        margin-bottom: 16px;
    }
}
.stops-instruction {
    margin: 0 0 24px;
}
.stops-tabs {
    position: relative;
    background-color: $color-white;
    padding: 16px;
    border: 1px solid $color-black-8;
    border-radius: 15px;
    margin: 0 -24px;
    @include breakpoint(large) {
        display: grid;
        grid-template-columns: 40% calc(60% - 32px);
        gap: 32px;
        margin: 0;
        padding: 0;
    }
}
.stop-list {
    @include breakpoint(medium) {
        padding-left: 16px;
    }
}
.stop-title {
    @include rem-typo(20px);
    margin: 0;
    padding: 16px;
}
.map-title {
    @include rem-typo(20px);
    margin: 0;
    padding: 16px 16px 16px 0;
}
.map-container {
    width: 100%;
    height: 450px;
    @include breakpoint(large) {
        height: 100vh;
        position: sticky;
        top: 0;
    }
}
.route-button-container {
    position: relative;
}
.route-search-container {
    position: absolute;
    top: 100%;
    right: 0;
    left: 0;
    width: 350px;
    max-width: calc(100vw - 28px);
    padding: 16px;
    background-color: $color-white;
    margin-top: 16px;
    border-radius: 15px;
    z-index: 1;
    .btn-is-open & {
        box-shadow: $dropdown-shadow;
    }
    @include breakpoint(medium) {
        border-radius: 20px;
    }
}
.no-stops-message {
    p {
        @include rem-typo(16px, 18px);
        font-weight: 300;
        text-align: center;
        @include breakpoint(medium) {
            @include rem-typo(20px, 32px);
            margin: 0;
        }
    }
    @include breakpoint(medium) {
        margin-bottom: 32px;
    }
}
</style>
